import React, { useContext, useEffect, useState } from "react";
import FormContainer from "components/form-container";
import ProgressBar from "components/progress-bar";
import Cookies from "js-cookie";
import {
  useLocation,
  useNavigate,
  createSearchParams,
  Link,
} from "react-router-dom";
import { Input } from "components/input";
import { getQueryParams } from "lib/utils";
import { checkPasswordCriteria, isValidEmail } from "lib/utils";
import { isLegoEmail } from "consts/lego-admin";
import AuthContext from "contexts/AuthContext";
import { PasswordValidation } from "components/password-validation";
import InputValidation from "components/input-validation";
import PasswordInput from "components/password-input";
import { useTranslation } from "react-i18next";
import { Address } from "components/address";
import { COUNTRY_NAME } from "consts/query-params";
import { useCurrentLocation } from "lib/utils";
import SingleCheckbox from "components/checkbox";
import LoadingSpinner from "components/loading-spinner";
import StreetNumberModal from "components/street-number-modal";
import MessageBanner from "components/message-banner";

export default function InStoreSignup() {
  const { t } = useTranslation();
  const { signupHandler, signupError } = useContext(AuthContext);
  const location = useLocation();
  const queryParams = getQueryParams(location.search);
  const { location: currentLocation } = useCurrentLocation();
  const [address, setAddress] = useState({
    addressLine1: ``,
    addressLine2: ``,
    addressCity: ``,
    addressCountry: queryParams[COUNTRY_NAME] || "US",
    addressZip: ``,
    addressState: ``,
  });
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState(
    () => localStorage.getItem("quickRegEmailInput") || ""
  );
  const [password, setPassword] = useState("");
  const [passwordAlarm, setPasswordAlarm] = useState(null);
  const [emailAlarm, setEmailAlarm] = useState(false);
  const [legoError, setLegoError] = useState(null);
  const [isSubscribedToMailReminders, setIsSubscribedToMailReminders] =
    useState(false);
  const [isSubscribedToSurvey, setIsSubscribedToSurvey] = useState(false);
  const [isNotInterested, setIsNotInterested] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showStreetNumberModal, setShowStreetNumberModal] = useState(false);

  useEffect(() => {
    if (isNotInterested) {
      setIsSubscribedToMailReminders(false);
      setIsSubscribedToSurvey(false);
    }
  }, [isNotInterested]);

  const handleSurveyCheckboxChange = (checked) => {
    setIsSubscribedToSurvey(checked);
    if (checked) setIsNotInterested(false);
  };
  const handleMailRemindersCheckboxChange = (checked) => {
    setIsSubscribedToMailReminders(checked);
    if (checked) setIsNotInterested(false);
  };
  const handleNotInterestedCheckboxChange = (checked) => {
    setIsNotInterested(checked);
    if (checked) {
      setIsSubscribedToSurvey(false);
      setIsSubscribedToMailReminders(false);
    }
  };

  const navigate = useNavigate();

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    setEmailAlarm(!isValidEmail(value));
    if (isLegoEmail(value)) {
      setLegoError(t("login.lego_alarm"));
    } else {
      setLegoError(null);
    }
  };
  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    setPasswordAlarm(!checkPasswordCriteria(value));
  };

  // Utility to check if addressLine1 contains a number
  const hasStreetNumber = (addressLine1) => /\d/.test(addressLine1);

  const doSignup = () => {
    setIsLoading(true);
    const isRestrictedLocationCookie = Cookies.get(`isRestrictedLocation`);
    if (isRestrictedLocationCookie) {
      alert(t(`ui.sign_up_geo`));
      setIsLoading(false);
      return;
    }
    const params = queryParams
      ? createSearchParams(queryParams).toString()
      : "";
    const submitData = {
      ...(firstName?.trim() && { firstName: firstName.trim() }),
      ...(lastName?.trim() && { lastName: lastName.trim() }),
      ...(email?.trim() && { email: email.trim() }),
      ...(password?.trim() && { password: password.trim() }),
      ...(address?.addressLine1?.trim() && {
        addressLine1: address.addressLine1.trim(),
      }),
      ...(address?.addressLine2?.trim() && {
        addressLine2: address.addressLine2.trim(),
      }),
      ...(address?.addressCity?.trim() && {
        addressCity: address.addressCity.trim(),
      }),
      ...(address?.addressZip?.trim() && {
        addressZip: address.addressZip.trim(),
      }),
      ...(address?.addressState?.trim() && {
        addressState: address.addressState.trim(),
      }),
      addressCountry: "US",
      isSubscribedToSurvey,
      isSubscribedToReminders: isSubscribedToMailReminders,
      redirectTo:
        queryParams?.redirectTo ||
        `${window.location.origin}${t(`urls.signup_store_sign_in_exists`)}?${encodeURIComponent(params)}`,
    };
    signupHandler(submitData, {
      onSuccess: () => {
        localStorage.setItem("quickRegEmailInput", email);
        const navigateOptions = {
          pathname: t("urls.signup_store_email_confirm"),
        };
        if (queryParams) {
          navigateOptions.search = createSearchParams(queryParams).toString();
        }
        navigate(navigateOptions);
        navigate(0);
        setTimeout(() => {
          setIsLoading(false);
        }, 500);
      },
      onError: (error) => {
        setIsLoading(false);
        if (error.message === `409`) {
          alert(`That email address is already in use. Please try again.`);
        } else if (error.message === `400`) {
          alert(`Password too weak. Please try again.`);
        } else {
          console.error(error);
          alert(`That didn't work. Please try again.`);
        }
      },
    });
  };

  const handleSubmit = () => {
    // If addressLine1 is missing a number, show modal and block sign-up
    if (!hasStreetNumber(address.addressLine1)) {
      setShowStreetNumberModal(true);
      return;
    }
    doSignup();
  };

  // Helper to bypass street number check (used after modal confirmation)
  const handleSubmitBypassStreetNumber = () => {
    doSignup();
  };

  // Handler for "Create Account" in modal: continue sign-up
  const handleModalCreateAccount = () => {
    setShowStreetNumberModal(false);
    // Call handleSubmit again, but this time allow sign-up to proceed
    handleSubmitBypassStreetNumber();
  };

  // Handler for "Complete Address" in modal: close modal, abort sign-up
  const handleModalCompleteAddress = () => {
    setShowStreetNumberModal(false);
    // Do not proceed with sign-up
  };

  const allFieldsValid =
    firstName?.trim() &&
    lastName?.trim() &&
    email?.trim() &&
    password?.trim() &&
    address?.addressLine1?.trim() &&
    address?.addressCity?.trim() &&
    address?.addressZip?.trim() &&
    address?.addressState?.trim() &&
    !passwordAlarm &&
    !emailAlarm &&
    !legoError &&
    (isSubscribedToSurvey || isSubscribedToMailReminders || isNotInterested);

  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "calc(100vh - 88px)",
        }}
      >
        <LoadingSpinner isLoading={isLoading} />
      </div>
    );
  }

  return (
    <>
      {showStreetNumberModal && (
        <StreetNumberModal
          setModalOpen={() => setShowStreetNumberModal(false)}
          modalOpen={showStreetNumberModal}
          onCreateAccount={handleModalCreateAccount}
          onCompleteAddress={handleModalCompleteAddress}
        />
      )}
      <FormContainer
        submitLabel="Create Account"
        onSubmit={handleSubmit}
        submitDisabled={!allFieldsValid}
        secondaryLabel={t("ui.button_back")}
        secondaryHref={`${t("urls.signup_store_email")}`}
        secondaryParams={queryParams}
      >
        <ProgressBar step={2} stepsNum={3} />
        <h2 className="font-semibold text-6xl max-w-lg">
          {t("trade-in.sign_up.title")}
        </h2>
        <div className="w-full flex flex-col gap-x-large">
          <div>
            <MessageBanner colour="blue" className="mb-x-large">
              <div className="flex items-center gap-2 text-sm">
                <span className="material-symbols-outlined">info</span>
                If you signed up last year, you'll need to sign up again to
                participate this year.
              </div>
            </MessageBanner>
            <Input
              label={t("ui.input_email")}
              type="email"
              id="email"
              name="email"
              value={email}
              onChange={(e) => handleEmailChange(e)}
              showWarning={legoError || emailAlarm || !email}
              warningMessage={
                legoError
                  ? "Sorry! LEGO.com email addresses cannot be used to create an account."
                  : "Please enter a valid email address"
              }
              warningOnlyAfterBlur
            />
          </div>
          <div>
            <PasswordInput
              label={t("ui.input_password")}
              id="password"
              name="password"
              value={password}
              onChange={handlePasswordChange}
              showWarning={passwordAlarm || !password}
              warningOnlyAfterBlur
            />
            <PasswordValidation passwordAlarm={passwordAlarm} />
          </div>
          <div className="flex flex-col sm:grid grid-cols-2 gap-y-x-large sm:gap-x-x-large">
            <Input
              label={t("ui.input_first_name")}
              type="text"
              id="firstName"
              name="firstName"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              autoComplete="given-name"
              showWarning={!firstName}
              warningMessage={!firstName ? "This field is required" : null}
              warningOnlyAfterBlur
            />
            <Input
              label={t("ui.input_last_name")}
              type="text"
              id="lastName"
              name="lastName"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              autoComplete="family-name"
              showWarning={!lastName}
              warningMessage={!lastName ? "This field is required" : null}
              warningOnlyAfterBlur
            />
          </div>
          <Address
            address={address}
            setAddress={setAddress}
            currentLocation={currentLocation}
          />
          <InputValidation errorMessage={signupError?.message} />

          <div className="flex flex-col gap-x-large">
            <div>
              <p className="text-xl font-bold">
                {t("sign-up-email-preferences.title")}
              </p>
              <p className="text-base  text-secondary">
                {t("sign-up-email-preferences.subtitle")}
              </p>
            </div>
            {/* <div>
            <p className="text-base text-secondary -mb-medium">
              {t("email-preferences.selectOne")}
            </p>
          </div> */}
            <p className="text-secondary text-base">
              <SingleCheckbox
                label={t("sign-up-email-preferences.marketingOption")}
                checked={isSubscribedToSurvey}
                setChecked={handleSurveyCheckboxChange}
                disabled={isNotInterested}
              />
            </p>
            <p className="text-secondary text-base">
              <SingleCheckbox
                label={t("sign-up-email-preferences.updatesOption")}
                checked={isSubscribedToMailReminders}
                setChecked={handleMailRemindersCheckboxChange}
                disabled={isNotInterested}
              />
            </p>
            <p className="text-secondary text-base">
              <SingleCheckbox
                label={t("sign-up-email-preferences.selectAll")}
                checked={isNotInterested}
                setChecked={handleNotInterestedCheckboxChange}
              />
            </p>
          </div>
          <div className="text-sm mt-x-large">
            By clicking "<strong>Create Account</strong>", I agree to the terms
            and conditions and the processing of my data in accordance with the{" "}
            <Link
              to={t("urls.privacy_policy")}
              className="text-blue"
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Policy
            </Link>
            . I can manage my email communication preferences at any time by
            going to Manage Account and updating my preferences.
          </div>
        </div>
      </FormContainer>
    </>
  );
}
